import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { Maximize2, Minimize2, RefreshCw, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import type { GradingGridRow } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { PrimaryContext } from '@/services/context';
import {
  onFarmImplantFreshEmbryosOptions,
  onFarmImplantFreshMatingsOptions,
  type OnFarmImplantRecordKey,
  toRecordKey,
} from '@/services/on-farm-implant';

import { type FreshMatingRow, useFreshMatingColumnDefs } from './columnDefs';
import { FreshMatingDetail } from './FreshMatingDetail';
import { FreshTabContext, type FreshTabContextValue } from './FreshTabContext';

type FreshTabProps = {
  sessionId: string | null;
  records: OnFarmImplantRecordKey[];
  readOnly?: boolean;
};

type FreshDraft = {
  nextStrawNumber: string;
  gradingByMating: Record<string, GradingGridRow[]>;
};

const draftStorageKey = (sessionId: string) =>
  `on-farm-implant-fresh-draft:${sessionId}`;

const readDraft = (sessionId: string | null): FreshDraft => {
  const empty: FreshDraft = { nextStrawNumber: '', gradingByMating: {} };
  if (!sessionId) return empty;
  try {
    const stored = sessionStorage.getItem(draftStorageKey(sessionId));
    return stored
      ? { ...empty, ...(JSON.parse(stored) as Partial<FreshDraft>) }
      : empty;
  } catch {
    return empty;
  }
};

export const FreshTab = ({
  sessionId,
  records,
  readOnly = false,
}: FreshTabProps) => {
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const gridRef = useRef<AgGridReact>(null);
  const columnDefs = useFreshMatingColumnDefs();
  // Drafts live here, not in the detail rows, because AG Grid recreates detail
  // rows on data refresh; sessionStorage keeps them across tab switches.
  const [draft, setDraft] = useState(() => readDraft(sessionId));
  const [searchText, setSearchText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const hasWork = !!sessionId && records.length > 0;
  const {
    data: matings = [],
    isLoading,
    isFetching,
    refetch: refetchMatings,
  } = useQuery({
    ...onFarmImplantFreshMatingsOptions(sessionId ?? ''),
    enabled: hasWork,
    refetchOnWindowFocus: false,
  });
  const { data: embryos = [], refetch: refetchEmbryos } = useQuery({
    ...onFarmImplantFreshEmbryosOptions(sessionId ?? ''),
    enabled: hasWork,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!sessionId) return;
    sessionStorage.setItem(draftStorageKey(sessionId), JSON.stringify(draft));
  }, [sessionId, draft]);

  const setNextStrawNumber = useCallback(
    (value: string) =>
      setDraft((current) => ({ ...current, nextStrawNumber: value })),
    [],
  );

  const setMatingGrading = useCallback(
    (matingKey: string, gradingRows: GradingGridRow[]) =>
      setDraft((current) => ({
        ...current,
        gradingByMating: {
          ...current.gradingByMating,
          [matingKey]: gradingRows,
        },
      })),
    [],
  );

  const { nextStrawNumber, gradingByMating } = draft;

  const rows = useMemo<FreshMatingRow[]>(
    () =>
      matings.map((mating) => {
        const matingEmbryos = embryos.filter(
          (embryo) =>
            embryo.matingId === mating.id &&
            (embryo.accountId ?? null) === (mating.accountId ?? null),
        );
        return {
          ...mating,
          createdCount: matingEmbryos.length,
          queuedCount: matingEmbryos.filter(
            (embryo) => embryo.queueState === 'Implanted',
          ).length,
          discardedCount: matingEmbryos.filter(
            (embryo) => embryo.queueState === 'Discarded',
          ).length,
        };
      }),
    [matings, embryos],
  );

  const contextValue = useMemo<FreshTabContextValue>(
    () => ({
      sessionId: sessionId ?? '',
      readOnly,
      embryos,
      nextStrawNumber,
      setNextStrawNumber,
      gradingByMating,
      setMatingGrading,
    }),
    [
      sessionId,
      readOnly,
      embryos,
      nextStrawNumber,
      setNextStrawNumber,
      gradingByMating,
      setMatingGrading,
    ],
  );

  const handleRefresh = useCallback(() => {
    void refetchMatings();
    void refetchEmbryos();
  }, [refetchMatings, refetchEmbryos]);

  const handleExpandCollapse = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (isExpanded) {
      api.forEachNode((node) => {
        if (node.master) node.setExpanded(false);
      });
      setIsExpanded(false);
      return;
    }
    const selected = api.getSelectedNodes();
    if (selected.length > 0) {
      selected.forEach((node) => node.setExpanded(true));
    } else {
      api.forEachNode((node) => {
        if (node.master) node.setExpanded(true);
      });
    }
    setIsExpanded(true);
  }, [isExpanded]);

  if (records.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-500">
        <i className="pi pi-inbox mb-3 text-3xl" />
        <h1 className="text-base font-semibold text-gray-800">
          {t('onFarmImplant.fresh.noWorkTitle')}
        </h1>
        <p className="mt-1 text-sm">{t('onFarmImplant.fresh.noWorkBody')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner" />
        {t('onFarmImplant.fresh.loading')}
      </div>
    );
  }

  return (
    <FreshTabContext.Provider value={contextValue}>
      <div className="flex flex-1 flex-col gap-1.5 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <label
            htmlFor="next-straw-number"
            className="text-muted-foreground text-xs font-medium whitespace-nowrap"
          >
            {t('onFarmImplant.fresh.nextStrawNumber')}
          </label>
          <Input
            id="next-straw-number"
            type="text"
            inputMode="numeric"
            disabled={readOnly}
            value={nextStrawNumber}
            onChange={(event) =>
              setNextStrawNumber(event.target.value.replace(/\D/g, ''))
            }
            className="w-32"
          />
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
            <Input
              placeholder={t('onFarmImplant.fresh.searchMatings')}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              className="min-w-44 pl-8"
            />
          </div>
          <span className="text-muted-foreground text-xs">
            {t('onFarmImplant.fresh.loadedCount', { count: rows.length })}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isFetching}
            >
              <RefreshCw
                className={`size-3.5 ${isFetching ? 'animate-spin' : ''}`}
              />
              {t('onFarmImplant.fresh.refresh')}
            </Button>
            <Button variant="outline" onClick={handleExpandCollapse}>
              {isExpanded ? (
                <Minimize2 className="size-3.5" />
              ) : (
                <Maximize2 className="size-3.5" />
              )}
              {isExpanded
                ? t('onFarmImplant.fresh.collapse')
                : selectedCount > 0
                  ? t('onFarmImplant.fresh.expandSelected', {
                      count: selectedCount,
                    })
                  : t('onFarmImplant.fresh.expand')}
            </Button>
          </div>
        </div>

        <div
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-18rem)] min-h-[400px] w-full`}
        >
          <WithLocalStorage storageKey="on-farm-implant-fresh-grid">
            <AgGridReact
              ref={gridRef}
              gridOptions={gridOptions}
              columnDefs={columnDefs as AgGridReactProps['columnDefs']}
              defaultColDef={defaultColDef}
              rowData={rows}
              rowSelection="multiple"
              suppressRowClickSelection
              onSelectionChanged={(event) =>
                setSelectedCount(event.api.getSelectedNodes().length)
              }
              quickFilterText={searchText}
              masterDetail
              isRowMaster={() => true}
              detailCellRenderer={FreshMatingDetail}
              detailRowAutoHeight
              rowHeight={40}
              animateRows={false}
              getRowId={getRowId<FreshMatingRow>((params) =>
                toRecordKey(params.data.id, params.data.accountId),
              )}
            />
          </WithLocalStorage>
        </div>
      </div>
    </FreshTabContext.Provider>
  );
};
